(function () {
  'use strict';

  // ---------------------------------------------------------------------
  // Shipping Option Picker
  // ---------------------------------------------------------------------
  // Pairs with `snippets/shipping-option-picker.liquid` and the dual-span
  // info-wrapper rendered by `snippets/variant-picker.liquid`:
  //
  //   <variant-picker ...>
  //     <div data-shipping-option-picker ...>            <-- this script's container
  //       <script data-bto-shipping-data>{...}</script>
  //       <input type="radio" value="standard_ocean" ...>
  //       <input type="radio" value="express_air" ...>
  //     </div>
  //     <info-wrapper>
  //       <span class="pdp-shipping-info" data-shipping="standard_ocean">...</span>
  //       <span class="pdp-shipping-info" data-shipping="express_air" hidden>...</span>
  //     </info-wrapper>
  //   </variant-picker>
  //
  // Responsibilities:
  //   1. Read per-variant surcharge amounts (already market-resolved by
  //      Liquid) from the injected JSON block.
  //   2. On initial load and on every `variant:change` event, update the
  //      Express Air label with the current variant's surcharge amount
  //      formatted in the active market's currency.
  //   3. Keep the picker visible whenever it was rendered (the Liquid
  //      BD + out-of-stock + invalid-incoming gate already decided that);
  //      show/hide the Ocean and Express Air swatches PER VARIANT, driven
  //      by the variant's `mode` in the JSON payload. That mode is resolved
  //      server-side by snippets/inbound-mode.liquid from the variant's
  //      custom.express_air_surcharge (the 'Ocean' / 'Air' product tags are
  //      no longer read). `data-inbound-mode` on the container is only the
  //      INITIAL variant's mode and serves as a fallback.
  //        - "ocean": Standard Ocean swatch only (Air hidden). Metafield blank.
  //        - "air":   Express Air swatch only (Ocean hidden), forced selected;
  //                   shows "(Free)" — the metafield resolves to 0. The
  //                   manufacturer-sold-out / incoming gates do not apply.
  //        - "both":  Ocean default; Express Air shown only when the
  //                   variant is not manufacturer-sold-out / covered-by-
  //                   incoming (its surcharge is > 0 by definition).
  //   4. Track the user's radio selection and keep the `is-selected`
  //      class + `data-selected-label` text in sync.
  //   5. Toggle the dual-span shipping summary (data-shipping attribute
  //      matches the radio value: "standard_ocean" or "express_air"),
  //      scoped to the nearest <variant-picker> ancestor so multiple
  //      pickers on one page (bundle-form, recommendations) do not
  //      cross-toggle each other.
  //   5b. Toggle the pre-order discount UI. The BD pre-order discount is not
  //      applied at checkout to a line shipping Express Air, so while Air is
  //      selected the price shows plain MSRP, the discount badge hides and the
  //      CTA drops its "— n% Off" suffix. price-list / product-badges /
  //      buy-buttons render both states and mark them
  //      data-preorder-discount-ui / data-preorder-regular-ui, keyed by the
  //      product form id. See snippets/preorder-air-default.liquid for the
  //      matching first-paint state.
  //   6. Persist the USER's selection across AJAX re-renders triggered
  //      by variant changes. Selection persists within the page session
  //      (module scope) and is keyed by product id. Selections this script
  //      forces itself (Ocean-only / Air-only variants) are not remembered,
  //      and a remembered value is only restored when the variant actually
  //      offers that swatch — so a customer who picked Ocean on a 'both'
  //      variant, passed through an Air-only one, and came back still has
  //      Ocean.
  //
  // All radio lookups here are scoped to `container` (the
  // data-shipping-option-picker fieldset), never filtered by the radio's
  // `name` attribute — shipping-option-picker.liquid gives bundle-context
  // radios a per-product name (to keep the browser from natively grouping
  // every bundle card's radios together), so the exact name string is not
  // constant across call sites. The fieldset only ever contains its own
  // Ocean/Air radios, so container scoping alone is already unambiguous.
  //
  // Non-responsibilities:
  //   * Does NOT set prices, bundle IDs, or line item properties beyond
  //     the `_shipping_option` radio input. Cart Transform Function
  //     handles all server-side money logic.
  //   * Does NOT manipulate the Add-to-cart form. The radio input is
  //     wired to the form via the `form` attribute.
  // ---------------------------------------------------------------------

  // Selection memory, keyed by productId. Lives for the duration of the
  // page; not persisted to storage. Reset on navigation/refresh.
  var selectionByProductId = {};

  function init(container) {
    if (container.dataset.btoShippingInitialized === 'true') {
      return;
    }
    container.dataset.btoShippingInitialized = 'true';

    var currencyCode = container.getAttribute('data-currency-code') || 'USD';
    var initialVariantId = container.getAttribute('data-initial-variant-id');
    var productId = container.getAttribute('data-product-id');
    var isReadonly = container.hasAttribute('data-readonly');

    var dataScript = findDataScriptNear(container);
    if (!dataScript) {
      return;
    }

    var data;
    try {
      data = JSON.parse(dataScript.textContent);
    } catch (e) {
      console.warn('[shipping-option] Failed to parse shipping data', e);
      return;
    }

    var priceDisplay = container.querySelector('[data-surcharge-display]');
    var selectedLabel = container.querySelector('[data-selected-label]');
    var radios = container.querySelectorAll('input[type="radio"]');
    var swatches = container.querySelectorAll('[data-shipping-swatch]');

    var formatter = createFormatter(currencyCode);

    function getEntryFor(variantId) {
      if (!variantId) return null;
      return data[variantId] || data[String(variantId)] || null;
    }

    function getSurchargeFor(variantId) {
      var entry = getEntryFor(variantId);
      if (!entry) return 0;
      var n = parseFloat(entry.surcharge);
      return isFinite(n) && n > 0 ? n : 0;
    }

    function isManufacturerSoldOut(variantId) {
      var entry = getEntryFor(variantId);
      return !!(entry && entry.manufacturerSoldOut === true);
    }

    function isHideAirForIncoming(variantId) {
      var entry = getEntryFor(variantId);
      return !!(entry && entry.hideAirForIncoming === true);
    }

    var airSwatch = container.querySelector('[data-shipping-swatch="express_air"]');
    var oceanSwatch = container.querySelector('[data-shipping-swatch="standard_ocean"]');

    function setAirSwatchVisible(visible) {
      if (!airSwatch) return;
      airSwatch.hidden = !visible;
    }

    function setOceanSwatchVisible(visible) {
      if (!oceanSwatch) return;
      oceanSwatch.hidden = !visible;
    }

    function getModeFor(variantId) {
      // Per-variant mode from the payload (snippets/inbound-mode.liquid). The
      // container attribute is the INITIAL variant's mode — a fallback for a
      // payload entry that is missing, never the source of truth.
      var entry = getEntryFor(variantId);
      var mode = entry && entry.mode;
      if (mode !== 'ocean' && mode !== 'air' && mode !== 'both') {
        mode = container.getAttribute('data-inbound-mode') || 'both';
      }
      return mode;
    }

    // Does the picker apply to this variant at all? Liquid decides it for the variant the page
    // rendered with; the payload carries the same answer for every other one
    // (snippets/inbound-option-state.liquid, the shared gate). Unknown means do not hide — an
    // entry can be missing, and a picker that vanishes is worse than one that lingers.
    function pickerAppliesTo(variantId) {
      var entry = getEntryFor(variantId);
      if (!entry || typeof entry.picker !== 'boolean') return true;
      return entry.picker;
    }

    function updateForVariant(variantId) {
      var mode = getModeFor(variantId);
      var surcharge = getSurchargeFor(variantId);

      // A bundle card changes variant without re-rendering, so Liquid's decision only ever
      // covers the variant the card first drew. Switching to one with no lanes at all — an
      // in-stock variant, say — used to leave the Standard Ocean swatch sitting there. Hide the
      // whole picker and uncheck it, so bundle-form.js (which reads `input:checked`) stops
      // putting _shipping_option on a line that has no lane to choose.
      if (!pickerAppliesTo(variantId)) {
        container.hidden = true;
        radios.forEach(function (radio) { radio.checked = false; });
        return;
      }

      container.hidden = false;

      // Ocean-only: the variant has no Express Air surcharge metafield.
      if (mode === 'ocean') {
        setOceanSwatchVisible(true);
        setAirSwatchVisible(false);
        forceOcean();
        setText(priceDisplay, '');
        return;
      }

      // Air-only: the metafield is present but resolves to 0 — Express Air is
      // free and the ONLY option, so Ocean is hidden and Air is forced
      // selected. The manufacturer-sold-out / incoming gates do not apply:
      // hiding Air here would leave the picker with no swatch at all.
      if (mode === 'air') {
        setOceanSwatchVisible(false);
        setAirSwatchVisible(true);
        forceAir();
        setText(priceDisplay, surcharge > 0
          ? '(+' + formatter.format(surcharge) + ')'
          : '(Free)');
        return;
      }

      // mode === 'both': Ocean default + Express Air when Air actually offers
      // an advantage. When it does not, hide ONLY the Express Air swatch and
      // force Ocean — the picker itself stays visible ("Inbound Option:
      // Standard Ocean").
      //
      // The surcharge is NOT part of that test. Since 2026-10-03 'both' also
      // covers a free Air lane on a variant that has a pre-order rate, so a
      // surcharge of 0 means "(Free)" here exactly as it does in the air-only
      // branch above — it never means "no Air". What can still take the swatch
      // away is a lane that buys no speed: the manufacturer committed to Ocean
      // only, or a covering batch lands before the flight.
      setOceanSwatchVisible(true);

      // 'both' has no forced default of its own — the server renders Ocean checked and the
      // customer's choice is left alone from then on. Nothing checked means the picker was
      // hidden for a variant with no lanes, which clears the radios, and has now come back.
      // Ocean is the default to come back to.
      if (!container.querySelector('input[type="radio"]:checked')) {
        forceOcean();
      }

      var mfrSoldOut = isManufacturerSoldOut(variantId);
      var hideForIncoming = isHideAirForIncoming(variantId);

      if (!mfrSoldOut && !hideForIncoming) {
        setAirSwatchVisible(true);
        setText(priceDisplay, surcharge > 0
          ? '(+' + formatter.format(surcharge) + ')'
          : '(Free)');
      } else {
        setAirSwatchVisible(false);
        forceOcean();
        setText(priceDisplay, '');
      }
    }

    // Selections this script forces (the variant offers only one option) are
    // not the customer's choice, so the radio `change` listener below skips
    // rememberSelection while this flag is up. Everything else that listens
    // to the event (bundle-shipping-sync, .bundle_form) still runs.
    var isForcing = false;

    function forceRadio(value) {
      var radio = container.querySelector(
        'input[type="radio"][value="' + value + '"]'
      );
      if (radio && !radio.checked) {
        isForcing = true;
        try {
          radio.checked = true;
          radio.dispatchEvent(new Event('change', { bubbles: true }));
        } finally {
          isForcing = false;
        }
      }
      syncSelectedUI();
    }

    function forceOcean() {
      forceRadio('standard_ocean');
    }

    function forceAir() {
      forceRadio('express_air');
    }

    function syncSelectedUI() {
      var checked = container.querySelector('input[type="radio"]:checked');

      swatches.forEach(function (swatch) {
        swatch.classList.remove('is-selected');
      });

      if (!checked) return;

      var matching = container.querySelector(
        '[data-shipping-swatch="' + escapeAttr(checked.value) + '"]'
      );
      if (matching) {
        matching.classList.add('is-selected');
      }

      // Read-only pickers (snippets/bundle-inbound-option.liquid) show a
      // state, not a choice — hide the non-selected swatch directly, on
      // this specific element, rather than through a CSS rule keyed on the
      // site-wide .block-swatch class (see assets/theme.css for why that
      // was expensive: it forced the browser to re-check every swatch on
      // the page, not just this picker's, on every selection change).
      if (isReadonly) {
        swatches.forEach(function (swatch) {
          swatch.hidden = swatch !== matching;
        });
      }

      setText(selectedLabel, checked.value === 'express_air'
        ? 'Express Air'
        : 'Standard Ocean');

      toggleSummary(container, checked.value);
      togglePreorderDiscountUI(container, checked.value);
    }

    function rememberSelection() {
      if (!productId) return;
      var checked = container.querySelector('input[type="radio"]:checked');
      selectionByProductId[productId] = checked ? checked.value : '';
    }

    function restoreSelectionIfRemembered() {
      if (!productId) return;
      if (!(productId in selectionByProductId)) return;
      var saved = selectionByProductId[productId];
      var target = container.querySelector(
        'input[type="radio"][value="' + escapeAttr(saved) + '"]'
      );
      if (!target || target.checked) return;
      // Only restore an option the CURRENT variant offers. updateForVariant has
      // already hidden the swatch this variant cannot use (Ocean on an Air-only
      // variant, Air on an Ocean-only one); checking its radio anyway would
      // submit a _shipping_option the variant cannot honour.
      var swatch = target.closest('[data-shipping-swatch]');
      if (swatch && swatch.hidden) return;
      target.checked = true;
      target.dispatchEvent(new Event('change', { bubbles: true }));
    }

    radios.forEach(function (radio) {
      radio.addEventListener('change', function (event) {
        // Inside a bundle grid, this radio's `change` would otherwise bubble up
        // to `.bundle_form`'s own listener (assets/bundle-form.js.liquid,
        // updateForm), which re-evaluates the bundle's composition from its
        // checkboxes. An Inbound Option pick changes no composition, so the
        // work is pointless there. It used to be worse than pointless: that
        // handler also recalculated a bundle total by reading each card's
        // rendered price, and choked on this event's target — the total was
        // removed on 2026-09-23 and the crash with it, but the guard stays
        // because the handler still has nothing to do with this radio. Leaves
        // the real PDP form (never `.bundle_form`) completely unaffected.
        if (container.closest('.bundle_form')) {
          event.stopPropagation();
        }
        if (!isForcing) {
          rememberSelection();
        }
        syncSelectedUI();
      });
    });

    // A bundle card's <select> is what the shopper reads and what bundle-form.js turns into the
    // cart payload, so it is the authority on which variant this card is about. Liquid now
    // preselects the right option, but reconciling here as well means the two can never disagree
    // — which is the shape the 2026-10-03 bug took: a picker describing the PDP's variant beside
    // a dropdown naming the first one. Only bundle cards are affected; a <product-card> holding
    // a picker happens nowhere else.
    var cardForVariant = container.closest && container.closest('product-card');
    if (cardForVariant) {
      var cardSelect = cardForVariant.querySelector('select');
      if (cardSelect && cardSelect.value) {
        initialVariantId = cardSelect.value;
        container.setAttribute('data-initial-variant-id', initialVariantId);
        showCardPriceFor(cardForVariant, initialVariantId);
      }
    }

    if (initialVariantId) {
      updateForVariant(initialVariantId);
    }
    restoreSelectionIfRemembered();
    syncSelectedUI();

    // Re-derives every piece of UI this picker owns from whichever radio is currently
    // checked. syncAll() below calls it after anything that could have replaced markup on
    // either side of the picker; see the note there for why that is necessary.
    container.btoResyncShippingOption = syncSelectedUI;

    // Can this picker's CURRENT variant actually honour `value`?
    //
    // A capability question, and deliberately NOT answered by looking at whether the swatch is
    // visible. assets/bundle-shipping-sync.js has to ask it before forcing an option onto a
    // bundle card, and it used to read the swatch's `hidden` instead — but a read-only card
    // hides every swatch except the selected one (syncSelectedUI above), so the swatch sync
    // needs to switch TO is always hidden there. One attribute meaning both "not offered" and
    // "not currently shown" silently killed sync for every read-only follower from 2026-09-20
    // (9d5ace03) until this was split out.
    //
    // The payload is the authority, and these are the same rules updateForVariant draws the
    // swatches with — kept here rather than duplicated in the sync script so the two can never
    // disagree about what a variant offers.
    container.btoSupportsOption = function (value) {
      if (!pickerAppliesTo(initialVariantId)) return false;
      var mode = getModeFor(initialVariantId);
      if (value === 'standard_ocean') return mode !== 'air';
      if (value !== 'express_air') return false;
      if (mode === 'ocean') return false;
      if (mode === 'air') return true;
      // 'both': Air is offered unless the lane buys no speed.
      return !isManufacturerSoldOut(initialVariantId) && !isHideAirForIncoming(initialVariantId);
    };

    // Re-points this picker at a different variant of the same product.
    //
    // updateForVariant runs once at init, off data-initial-variant-id. On the PDP that is
    // enough: a variant change re-renders the section and the picker comes back with a new
    // attribute. A bundle card has no such flow — its variant lives in a plain <select> that
    // replaces no markup — so the swatches kept describing whichever variant the card first
    // rendered, with a different one selected in the dropdown. The attribute is updated too,
    // so a later syncAll() does not undo this.
    container.btoSetShippingVariant = function (variantId) {
      if (!variantId || variantId === initialVariantId) return;
      initialVariantId = variantId;
      container.setAttribute('data-initial-variant-id', variantId);
      updateForVariant(variantId);
      syncSelectedUI();
    };
  }

  // ---------------------------------------------------------------------
  // Dual-span summary toggle
  // ---------------------------------------------------------------------
  // Scoped to the picker's nearest <variant-picker> ancestor. The Liquid
  // dual-span uses data-shipping attribute values matching radio values:
  // "standard_ocean" and "express_air". A radio with an unknown value
  // hides all spans (defensive — should not happen in practice).
  //
  // Bundle cards (snippets/bundle-inbound-option.liquid) render a picker
  // without a <variant-picker> ancestor — they never emit the dual-span
  // summary at all — so `.closest('.bundle_form')` is checked first as an
  // equally-valid stopping point. Without it, a bundle card's picker falls
  // through all the way to `document` and toggles the REAL PDP picker's own
  // summary spans instead, corrupting the main product's displayed shipping
  // copy whenever the customer picks an option on an unrelated bundle card.
  // ---------------------------------------------------------------------
  // Pre-order discount UI toggle
  // ---------------------------------------------------------------------
  // Keyed by the product form id rather than by DOM ancestry: the price, the
  // badge and the CTA are siblings of the picker's block in product-info's
  // block loop, not descendants of <variant-picker>, and the sticky quick-add
  // bar sits outside the buy box entirely while sharing its form id. Matching
  // on the id reaches all of them and, just as importantly, reaches nothing
  // belonging to another buy box: the repeat buy box builds its own id
  // (product-form-{id}-bbr-{section}), so each box reflects its own selection.
  //
  // A picker whose form id owns no such elements — a bundle card, or a product
  // with no pre-order discount — simply matches nothing.
  function togglePreorderDiscountUI(container, optionValue) {
    var formId = container.getAttribute('data-product-form-id');
    if (!formId) return;

    var isAir = optionValue === 'express_air';
    var id = escapeAttr(formId);

    document
      .querySelectorAll('[data-preorder-discount-ui="' + id + '"]')
      .forEach(function (el) { el.hidden = isAir; });

    document
      .querySelectorAll('[data-preorder-regular-ui="' + id + '"]')
      .forEach(function (el) { el.hidden = !isAir; });

    // The CTA's brand-accent fill is a server-rendered <style> block, so it cannot react to a
    // radio by itself. buy-buttons.liquid scopes that rule to :not([data-air-selected]) and
    // marks the element here instead — Air then falls back to the theme's ordinary Add-to-cart
    // colours, matching the label the swap above puts there. An attribute rather than a class
    // so it cannot collide with whatever classes the button element already carries.
    document
      .querySelectorAll('[data-preorder-accent-ui="' + id + '"]')
      .forEach(function (el) {
        if (isAir) el.setAttribute('data-air-selected', '');
        else el.removeAttribute('data-air-selected');
      });
  }

  function toggleSummary(container, optionValue) {
    var scope = container.closest('variant-picker') || container.closest('.bundle_form') || document;
    var spans = scope.querySelectorAll('.pdp-shipping-info[data-shipping]');
    if (!spans.length) return;
    spans.forEach(function (span) {
      span.hidden = span.getAttribute('data-shipping') !== optionValue;
    });
  }

  // ---------------------------------------------------------------------
  // Global event wiring (outside init so it persists across re-renders)
  // ---------------------------------------------------------------------

  // ---------------------------------------------------------------------
  // Keeping the UI in step with the DOM
  // ---------------------------------------------------------------------
  // The selected shipping option lives in a radio, but what it controls now reaches outside
  // the picker: the dual-span shipping summary and — since the pre-order discount became
  // Air-dependent — the price, the discount badge and the CTA. Any of those, or the picker
  // itself, can be replaced independently by a section re-render, and a replacement always
  // arrives in its SERVER default state.
  //
  // If only the picker is replaced, its radio resets to Ocean while the price stays in the Air
  // state the customer last chose — quoting MSRP on a line that will be discounted. If only
  // the price is replaced, it comes back discounted while Air is still selected. Both were
  // reachable: this script used to listen for `variant:change` alone, and add-to-cart re-renders
  // the product form without one.
  //
  // So instead of reacting to a single event, re-derive everything from the live radios after
  // anything that could have moved markup. init() is idempotent, so a pass both adopts pickers
  // that appeared and re-syncs those that did not.
  function syncAll() {
    document.querySelectorAll('[data-shipping-option-picker]').forEach(function (container) {
      init(container);
      if (typeof container.btoResyncShippingOption === 'function') {
        container.btoResyncShippingOption();
      }
    });
  }

  var observer = null;
  var syncPending = false;

  function observeDom() {
    if (!observer || !document.body) return;
    // childList only. Syncing writes `hidden` attributes, so observing attributes would make
    // the observer react to its own work.
    observer.observe(document.body, { childList: true, subtree: true });
  }

  function runSync() {
    syncPending = false;
    // Syncing also replaces text nodes, which IS a childList mutation. Detach first and drop
    // whatever queued while we worked, or the pass schedules itself again forever.
    if (observer) observer.disconnect();
    try {
      syncAll();
    } finally {
      if (observer) {
        observer.takeRecords();
        observeDom();
      }
    }
  }

  function scheduleSync() {
    if (syncPending) return;
    syncPending = true;
    if (typeof requestAnimationFrame === 'function') {
      requestAnimationFrame(runSync);
    } else {
      setTimeout(runSync, 0);
    }
  }

  // variant:change is the picker's own flow. The rest fire around add-to-cart and cart updates,
  // which is where the stale-price case came from.
  ['variant:change', 'variant:add', 'product:rerender', 'cart:change', 'cart:refresh'].forEach(
    function (eventName) {
      document.addEventListener(eventName, scheduleSync);
    }
  );

  // Shows the one price belonging to this variant. product-card.liquid renders a price per
  // variant, all but one hidden, precisely so nothing here has to compute an amount — switching
  // visibility cannot disagree with the server the way a second calculation would.
  //
  // Independent of the picker: a bundle card for a non-BD product has prices and no picker, and
  // its dropdown still has to move them.
  function showCardPriceFor(card, variantId) {
    var prices = card.querySelectorAll('[data-card-price-variant]');
    if (!prices.length) return;
    var matched = false;
    prices.forEach(function (el) {
      var mine = el.getAttribute('data-card-price-variant') === String(variantId);
      el.hidden = !mine;
      if (mine) matched = true;
    });
    // A variant with no price rendered should not leave the card blank. Cannot happen while
    // product-card renders every variant, which is why it renders every variant.
    if (!matched) prices[0].hidden = false;
  }

  // A bundle card picks its variant with a <select>, which emits no variant:change and replaces
  // no markup, so the picker inside that card has to be told. Delegated from the document
  // because cards are rendered server-side in a loop and can also arrive later.
  //
  // Scoped by <product-card> ancestry rather than by product id: the same product can appear in
  // a bundle more than once, and only the card the dropdown belongs to should move. Everywhere
  // else this matches nothing, because a <product-card> containing a picker only happens in a
  // bundle.
  document.addEventListener('change', function (event) {
    var select = event.target;
    if (!select || select.tagName !== 'SELECT' || !select.closest) return;
    var card = select.closest('product-card');
    if (!card) return;
    var variantId = select.value;
    if (!variantId) return;
    showCardPriceFor(card, variantId);
    card.querySelectorAll('[data-shipping-option-picker]').forEach(function (picker) {
      init(picker);
      if (typeof picker.btoSetShippingVariant === 'function') {
        picker.btoSetShippingVariant(variantId);
      }
    });
  });

  if (typeof MutationObserver === 'function') {
    observer = new MutationObserver(scheduleSync);
  }

  // ---------------------------------------------------------------------
  // Utilities
  // ---------------------------------------------------------------------

  function findDataScriptNear(container) {
    var script = null;
    var parent = container.parentElement;
    while (parent && !script) {
      script = parent.querySelector('script[data-bto-shipping-data]');
      parent = parent.parentElement;
    }
    return script;
  }

  function createFormatter(currencyCode) {
    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: currencyCode,
        currencyDisplay: 'narrowSymbol'
      });
    } catch (e) {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD'
      });
    }
  }

  function escapeAttr(value) {
    return String(value).replace(/"/g, '\\"');
  }

  // Writes only on a real change: an unconditional textContent assignment replaces the text
  // node even when the string is identical, which the MutationObserver above would see.
  function setText(element, value) {
    if (!element) return;
    if (element.textContent !== value) {
      element.textContent = value;
    }
  }

  function initAll() {
    var containers = document.querySelectorAll('[data-shipping-option-picker]');
    containers.forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initAll();
      observeDom();
    });
  } else {
    initAll();
    observeDom();
  }
})();